import { Banner, BannerState, BannerType } from '@/modules/adv/banner'
import { OutOfPageBanner } from '@/modules/adv/out-of-page-banner'
import { AdProviderNames, PlatformType } from '@/types'
import { timer } from '@/utils/helpers'
import { isElementViewable } from '@/utils/is-element-viewable'
import { loadScript } from '@/utils/loadScript'

import {
    AdProvider,
    type AdProviderConfig,
    AdProviderStatus,
    type AdUnitConfig,
    type OutOfPageAdType,
    type PageAdOptions,
    type PageAdType,
} from '../../'

export const GAM_SOURCE = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js'

const GAME_INTERSTITIAL_TIMEOUT = 120_000
const SINGLE_REQUEST_THROTTLE_TIMEOUT = 50
const BANNER_RENDER_TIMEOUT = 6_000

export class GoogleAdsService implements AdProvider {
    private readonly gpt: typeof googletag

    private readonly banners = new Map<googletag.Slot, Banner | OutOfPageBanner>()

    private readonly gamAccount: string

    private readonly adUnitConfig: AdUnitConfig | undefined
    private readonly useOnlyConfig: boolean

    private readonly currentTargeting: Record<string, string | string[]> = {}

    private isFirstRequestSent = false
    private firstAdRequestTimeout: ReturnType<typeof setTimeout> | undefined

    private readonly platform: PlatformType

    private readonly isWidget: boolean

    private formats: Record<OutOfPageAdType, googletag.enums.OutOfPageFormat> | null = null

    private gameManualInterstitialTriggeredAt = 0

    private bannerRenderTimeouts = new Map<googletag.Slot, number>()

    readonly serviceStatus: Promise<AdProviderStatus>

    readonly hasPrebidIntegration = true

    constructor({
        targeting = {},
        gamAccount,
        platform,
        auid,
        isWidget,
        adUnitConfig,
        useOnlyConfig,
    }: AdProviderConfig & { gamAccount: string }) {
        window.googletag = window.googletag || { cmd: [] }
        window.googletag.cmd = window.googletag.cmd || [] // it may be not empty after reload with cached resources

        this.gpt = window.googletag
        this.gamAccount = gamAccount

        if (adUnitConfig) {
            this.adUnitConfig = adUnitConfig
        }
        this.useOnlyConfig = Boolean(adUnitConfig && useOnlyConfig)

        this.platform = platform
        this.isWidget = isWidget

        this.serviceStatus = new Promise((resolve, reject) => {
            if (process.env.NODE_ENV === 'production') {
                loadScript({ src: GAM_SOURCE, useExisting: true, crossorigin: 'anonymous' }).catch(() =>
                    reject(AdProviderStatus.offline),
                )
            } else {
                // in dev mode the script is inserted by HeadMeta a bit later, so just wait a sec
                window.setTimeout(() => {
                    loadScript({ src: GAM_SOURCE, useExisting: true, crossorigin: 'anonymous' }).catch(() =>
                        reject(AdProviderStatus.offline),
                    )
                }, 1000)
            }

            this.updateTargeting(targeting)
            this.gpt.cmd.push(() => {
                this.formats = {
                    interstitial: this.gpt.enums.OutOfPageFormat.GAME_MANUAL_INTERSTITIAL,
                    interstitial_preroll: this.gpt.enums.OutOfPageFormat.GAME_MANUAL_INTERSTITIAL,
                    interstitial_preroll_2: this.gpt.enums.OutOfPageFormat.GAME_MANUAL_INTERSTITIAL,
                    rewarded: this.gpt.enums.OutOfPageFormat.REWARDED,
                    rewarded_2: this.gpt.enums.OutOfPageFormat.REWARDED,
                    sticky_portrait: this.gpt.enums.OutOfPageFormat.BOTTOM_ANCHOR,
                }

                this.gpt.pubads().setPublisherProvidedId(auid)
                this.gpt.setConfig({
                    centering: true,
                    singleRequest: true,
                    adsenseAttributes: {
                        page_url: 'playgama.com',
                    },
                })

                this.gpt.pubads().addEventListener('rewardedSlotReady', (event) => {
                    const banner = this.banners.get(event.slot) as OutOfPageBanner | undefined
                    banner?.triggerReady(event.makeRewardedVisible)
                })
                this.gpt.pubads().addEventListener('gameManualInterstitialSlotReady', (event) => {
                    const banner = this.banners.get(event.slot) as OutOfPageBanner | undefined
                    banner?.triggerReady(event.makeGameManualInterstitialVisible)
                    this.gameManualInterstitialTriggeredAt = Date.now()
                })

                this.gpt.pubads().addEventListener('rewardedSlotClosed', (event) => {
                    this.banners.get(event.slot)?.triggerClosed()
                })

                this.gpt.pubads().addEventListener('gameManualInterstitialSlotClosed', (event) => {
                    this.banners.get(event.slot)?.triggerClosed()
                })

                this.gpt.pubads().addEventListener('rewardedSlotGranted', (event) => {
                    this.banners.get(event.slot)?.triggerRewarded(event.payload)
                })

                this.gpt.pubads().addEventListener('impressionViewable', (event) => {
                    const timeout = this.bannerRenderTimeouts.get(event.slot)

                    if (typeof timeout === 'number') {
                        this.banners.get(event.slot)?.triggerRendered()
                        window.clearTimeout(timeout)
                        this.bannerRenderTimeouts.delete(event.slot)
                    }

                    this.banners.get(event.slot)?.triggerViewable()
                })

                this.gpt.pubads().addEventListener('slotRenderEnded', (event) => {
                    const banner = this.banners.get(event.slot)
                    if (event.isEmpty) {
                        banner?.triggerEmpty()
                    } else if (banner?.type === BannerType.outOfPageBanner) {
                        this.bannerRenderTimeouts.set(
                            event.slot,
                            window.setTimeout(() => {
                                banner?.triggerEmpty()
                                banner?.destroy()
                                this.bannerRenderTimeouts.delete(event.slot)
                            }, BANNER_RENDER_TIMEOUT),
                        )
                    } else {
                        banner?.triggerRendered()
                    }
                })

                this.gpt.enableServices()

                resolve(AdProviderStatus.online)
            })
        })

        window.addEventListener('resize', this.onViewportResize.bind(this))
    }

    // Since Google Ad doesn't seem to offer a proper way to handle viewport resizes gracefully,
    // we close the banner on the first resize event to avoid layout issues.
    private onViewportResize(): void {
        const banner = this.getOutOfPageBanner()
        if (!banner) {
            return
        }

        if (banner.state === BannerState.showing) {
            banner.triggerClosed()
        } else {
            banner.triggerEmpty()
            banner.destroy()
        }
    }

    private getOutOfPageBanner(): OutOfPageBanner | null {
        for (const b of this.banners.values()) {
            if (b.type === BannerType.outOfPageBanner) {
                return b as OutOfPageBanner
            }
        }
        return null
    }

    private throttleRequestAds(lastSlot: googletag.Slot) {
        if (this.firstAdRequestTimeout) {
            clearTimeout(this.firstAdRequestTimeout)
        }
        this.firstAdRequestTimeout = setTimeout(() => {
            this.gpt.cmd.push(() => {
                this.isFirstRequestSent = true
                this.gpt.display(lastSlot) // all slots will be displayed due to singleRequest
            })
        }, SINGLE_REQUEST_THROTTLE_TIMEOUT)
    }

    private preloadAd(type: OutOfPageAdType): Promise<OutOfPageBanner> {
        const adUnit = this.getAdUnitByType(type)
        let typeToLoad = type
        if (
            type === 'interstitial' &&
            Date.now() < this.gameManualInterstitialTriggeredAt + GAME_INTERSTITIAL_TIMEOUT
        ) {
            // preload rewarded instead of interstitial
            typeToLoad = 'rewarded'
        }

        return new Promise<OutOfPageBanner>((resolve, reject) => {
            let slot: googletag.Slot | null
            this.gpt.cmd.push(() => {
                slot = this.gpt.defineOutOfPageSlot(adUnit, this.formats![typeToLoad])

                // Slot returns null if the page or device does not support rewarded ads.
                if (!slot) {
                    reject(new Error('Failed to define out-of-page slot'))
                    return
                }

                const banner = new OutOfPageBanner({
                    provider: AdProviderNames.GOOGLE_AD,
                    adUnit,
                    destroy: () => {
                        this.banners.delete(slot!)
                        this.gpt.cmd.push(() => {
                            googletag.destroySlots([slot!])
                        })
                    },
                })

                this.banners.set(slot, banner)

                resolve(banner)

                slot.addService(this.gpt.pubads())

                this.gpt.display(slot)

                this.isFirstRequestSent = true

                banner.addEventListener('closed', () => {
                    banner.destroy()
                })
            })
        })
    }

    // https://developers.google.com/publisher-tag/samples/display-rewarded-ad

    public async prepareOutOfPageAd(type: OutOfPageAdType, timeout?: number): Promise<OutOfPageBanner> {
        // eslint-disable-next-line prefer-promise-reject-errors
        const rejectByTimeout: Promise<void> = timeout ? timer(timeout) : Promise.reject()
        const loadingBanner = await this.preloadAd(type) // an unprocessable slot triggers rejection

        rejectByTimeout.then(() => {
            if (loadingBanner.state === BannerState.loading) {
                console.info('empty by timeout, google')

                loadingBanner.triggerEmpty()
                loadingBanner.destroy()
            }
        })
        // duplicate banner state event
        switch (loadingBanner.state) {
            case BannerState.ready:
                window.setTimeout(() => {
                    loadingBanner.triggerReady()
                }, 16)
                break
            case BannerState.empty:
                window.setTimeout(() => {
                    loadingBanner.triggerEmpty()
                    loadingBanner.destroy()
                }, 16)
                break
            default:
                break
        }

        return loadingBanner
    }

    public requestPageAd(options: PageAdOptions, bannerTargeting: AdProviderConfig['targeting']): Promise<Banner> {
        const adUnit = this.getAdUnitByType(options.type)

        return new Promise((resolve, reject) => {
            this.gpt.cmd.push(() => {
                let refreshInterval: number
                let slot: googletag.Slot | null = null

                if (options.sizes) {
                    slot = this.gpt.defineSlot(adUnit, options.sizes, options.el)
                }

                if (!slot) {
                    console.error('Failed to define slot')
                    reject(new Error('Failed to define slot'))
                    return
                }

                if (bannerTargeting) {
                    slot.setConfig({
                        targeting: bannerTargeting,
                    })
                }

                slot.addService(this.gpt.pubads())
                if (this.isFirstRequestSent) {
                    this.gpt.display(slot)
                } else {
                    this.throttleRequestAds(slot)
                }

                if (options.refresh) {
                    refreshInterval = window.setInterval(async () => {
                        const shouldRefresh = await isElementViewable(slot.getSlotElementId())
                        if (shouldRefresh) {
                            this.gpt.pubads().refresh([slot])
                        }
                    }, options.refresh * 1000)
                }

                const banner = new Banner({
                    provider: AdProviderNames.GOOGLE_AD,
                    adUnit,
                    destroy: () => {
                        this.gpt.destroySlots([slot])
                        window.clearInterval(refreshInterval)
                        this.banners.delete(slot)
                    },
                })

                this.banners.set(slot, banner)

                resolve(banner)
            })
        })
    }

    public updateTargeting(targeting: AdProviderConfig['targeting'] = {}) {
        for (const key in targeting) {
            if (targeting[key]) {
                this.currentTargeting[key] = targeting[key]
            } else {
                delete this.currentTargeting[key]
            }
        }

        this.gpt.cmd.push(() => {
            this.gpt.setConfig({
                targeting: this.currentTargeting,
            })
        })
    }

    public getAdUnitByType(type: OutOfPageAdType | PageAdType): string {
        const prefix = this.isWidget ? 'widget_' : ''

        const isMobile = this.platform === 'ios' || this.platform === 'android'
        const simplifiedPlatform = isMobile ? 'mobile' : 'desktop'
        const suffix = `_${simplifiedPlatform}`

        const unitFromConfig = this.adUnitConfig?.[simplifiedPlatform]?.[type]
        if (unitFromConfig) {
            return `${this.gamAccount}/${unitFromConfig}`
        }

        if (this.useOnlyConfig) {
            throw new Error('No such ad unit in this provider')
        }

        // Base units that need platform suffix
        const baseUnits: Record<string, string> = {
            rewarded: 'rewarded',
            rewarded_2: 'rewarded',
            interstitial: 'h5_interstitial',
            interstitial_preroll: 'h5_interstitial_preroll',
            interstitial_preroll_2: 'h5_interstitial_preroll',
        }

        // If it's one of the base units that need platform suffix
        if (type in baseUnits) {
            return `${this.gamAccount}/${prefix}${baseUnits[type]}${suffix}`
        }

        // Static units that don't need platform suffix
        const staticUnits: Record<string, string> = {
            sticky_mobile_non_game_page: 'sticky_mobile',
            sticky_mobile: 'sticky_mobile',
            sticky_tablet: 'sticky_mobile',
            sticky_desktop: 'sticky_desktop',
            sidebar: 'sidebar',
            sidebar_bottom: 'sidebar_bottom',
            leaderboard: 'leaderboard',
            leaderboard_rest: 'leaderboard_rest',
            widget_sidebar: 'widget_sidebar',
            widget_sidebar_bottom: 'widget_sidebar_bottom',
            widget_horizontal: 'widget_horizontal',
            widget_horizontal_2: 'widget_horizontal_2',
            widget_sticky_mobile: 'widget_sticky_mobile',
            widget_sticky_tablet: 'widget_sticky_mobile',
        }

        return `${this.gamAccount}/${staticUnits[type]}`
    }
}
